import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';

import {
	ARCHIVED_PAGE_BANNER_EXPERIENCE,
	RESTORE_PAGES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import type { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { canRestoreContent } from '@confluence/content-types-utils/entry-points/contentOperationsUtils.ts';
import { LoadableAfterPaint, LoadableLazy } from '@confluence/loadable';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { fireUIClickEvent } from './AnalyticsEvents.tsx';
import type {
	ArchivedPageBannerV2ArchivedPageBannerQuery$data,
	ArchivedPageBannerV2ArchivedPageBannerQuery$variables,
} from './__generated__/ArchivedPageBannerV2ArchivedPageBannerQuery.graphql.ts';
import type {
	ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data,
	ArchivedPageBannerV2ArchivedPageBannerContainerQuery$variables,
} from './__generated__/ArchivedPageBannerV2ArchivedPageBannerContainerQuery.graphql.ts';
import type {
	ArchivedPageBannerV2Status_content$data,
	ArchivedPageBannerV2Status_content$key,
} from './__generated__/ArchivedPageBannerV2Status_content.graphql.ts';
import type {
	ArchivedPageBannerV2_content$data,
	ArchivedPageBannerV2_content$key,
} from './__generated__/ArchivedPageBannerV2_content.graphql.ts';
import {
	ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
	CONTENT_STATUS_ARCHIVED,
} from './archivingConstants.ts';

export const ArchivedPageBannerV2Status_content: GraphQLTaggedNode = graphql`
	fragment ArchivedPageBannerV2Status_content on Content {
		id
		status
	}
`;

export const ArchivedPageBannerV2_content: GraphQLTaggedNode = graphql`
	fragment ArchivedPageBannerV2_content on Content {
		id
		type
		operations {
			operation
			targetType
		}
		archivedContentMetadata {
			archiveNote
		}
		history {
			lastUpdated {
				by {
					... on KnownUser {
						accountId
					}
					... on UnknownUser {
						accountId
					}
				}
			}
		}
	}
`;

const LazyUnarchivePagesLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UnarchivePagesLoader" */ './UnarchivePagesLoader.tsx'
			)
		).UnarchivePagesLoader,
});

const LazyArchivedPageBannerContent = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ArchivedPageBannerContent" */ './ArchivedPageBannerContent.tsx'
			)
		).ArchivedPageBannerContent,
});

type ArchivedPageBannerProps = {
	contentId: string;
	marginStyle?: 'none' | 'bottom' | 'top' | 'both';
	contentFragmentRef?: ArchivedPageBannerV2Status_content$key | null;
	onPageRestore?: () => void | Promise<void>;
};

type ArchivedPageBannerStatusProps = Pick<ArchivedPageBannerProps, 'contentId' | 'marginStyle'> & {
	contentStatus: string | null | undefined;
	statusContentId: string | null | undefined;
	statusError?: Error;
	statusRefetch?: () => void;
	onPageRestore?: () => void | Promise<void>;
	useMetadataFragmentReader?: boolean;
};

type ArchivedPageBannerMetadataContent =
	| NonNullable<
			NonNullable<ArchivedPageBannerV2ArchivedPageBannerQuery$data['content']>['nodes']
	  >[number]
	| ArchivedPageBannerV2_content$data
	| null
	| undefined;

type RelayFragmentRef = {
	readonly ' $fragmentSpreads'?: Record<string, unknown>;
};

const hasFragmentSpread = (fragmentRef: unknown): fragmentRef is RelayFragmentRef =>
	Boolean((fragmentRef as RelayFragmentRef | null | undefined)?.[' $fragmentSpreads']);

const hasDirectField = (fragmentRef: unknown, fieldName: string): boolean =>
	Boolean(
		fragmentRef &&
		typeof fragmentRef === 'object' &&
		Object.prototype.hasOwnProperty.call(fragmentRef, fieldName),
	);

const isMatchingContentId = (value: string | null | undefined, contentId: string): boolean =>
	// oxlint-disable-next-line prefer-template
	value === contentId || value === 'Content:' + contentId;

type ArchivedPageBannerWithMetadataProps = ArchivedPageBannerStatusProps & {
	noteDataContent: ArchivedPageBannerMetadataContent;
	noteDataError?: Error;
};

const ArchivedPageBannerWithMetadata = ({
	contentId,
	marginStyle = 'bottom',
	contentStatus,
	statusContentId,
	statusError,
	statusRefetch,
	onPageRestore,
	noteDataContent,
	noteDataError,
}: ArchivedPageBannerWithMetadataProps) => {
	const isContentArchived = contentStatus === CONTENT_STATUS_ARCHIVED;
	// Note that when the user changes to a different page/content item, this component remains
	// mounted and will fetch the data for the new page, but the old status data can still be
	// available while the new data is being fetched. So the new data isn't really ready until
	// we see that the data has the correct contentId in it.
	const isStatusDataAvailable = contentStatus && isMatchingContentId(statusContentId, contentId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	// We need to keep the success atrributes object reference constant, as creating and assigning a
	// whole new object on every render pass will cause ExperienceSuccess's useEffect() to think
	// something has changed and it will fire the "success" event again. But if we keep the same
	// object across renders and just set the "archivedPageBannerShown" value, then ExperienceSuccess
	// sees no change there and will only fire again if something else has changed.
	const successAttributes: ExperienceAttributes = useMemo(
		() => ({
			archivedPageBannerShown: false,
		}),
		[],
	);

	const getUnarchiveDialogOpener = useCallback(
		(dialogs: DialogsStateContainer) => () => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'restoreButton',
				actionSubjectId: contentId,
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			});

			experienceTracker.start({
				name: RESTORE_PAGES_EXPERIENCE,
			});

			dialogs.showModal(LazyUnarchivePagesLoader, {
				contentId,
				onPageRestore: onPageRestore || statusRefetch,
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			});
		},
		[contentId, createAnalyticsEvent, experienceTracker, onPageRestore, statusRefetch],
	);

	const fireLearnMoreEvent = useCallback(
		() =>
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'link',
				actionSubjectId: 'restoreLearnMore',
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			}),
		[createAnalyticsEvent],
	);

	const userCanRestorePage = canRestoreContent([...(noteDataContent?.operations || [])]);

	const archiveNote = noteDataContent?.archivedContentMetadata?.archiveNote || '';
	// accountId does not exist on Anonymous user type, so we need to use bracket notation to get the property or undefined
	const archiverAccountId: string | null | undefined =
		noteDataContent?.history?.lastUpdated?.by?.['accountId'];

	// Similar to the status data, if the user changes to a different page/content item, this
	// component will remain mounted and will fetch the data for the new page, but the old "noteData"
	// is still available while the new data is being fetched. So the new data isn't really ready
	// until we see that the data has the correct contentId in it.
	const isNoteDataAvailable =
		noteDataContent && isMatchingContentId(noteDataContent?.id, contentId);

	// Only update the value; creating and assigning a whole new object will cause ExperienceSuccess's
	// useEffect() to think something has changed and it will fire the "success" event again.
	successAttributes.archivedPageBannerShown = isContentArchived;

	const isBannerRendered = isStatusDataAvailable && isContentArchived && !!noteDataContent;
	useEffect(() => {
		if (isBannerRendered) {
			addUFOCustomData({
				archivedPageBannerRendered: true,
			});
		}
	}, [isBannerRendered]);

	const error = statusError || noteDataError;
	return (
		<>
			{error && (
				<ErrorDisplay error={error}>
					<ExperienceFailure name={ARCHIVED_PAGE_BANNER_EXPERIENCE} error={error} />
				</ErrorDisplay>
			)}
			{/* Experience Success is when we've sucessfully gotten all the query data we need.
  We must have the status info (whether the content item is archived or not), and we only
  need the note data if the item is archived.
  (Note we are only successful if the specific data fields are retreived; if we get some
  data back but the specific data fields we need aren't returned, then that's a failure. */}
			{isStatusDataAvailable &&
				(!isContentArchived || !!isNoteDataAvailable) && (
					// Note that using a "key" prop with the "contentId" here will make React create a new
					// ExperienceSuccess instance, which is useful because we can remain mounted when the user
					// changes to a different content item
					<ExperienceSuccess
						name={ARCHIVED_PAGE_BANNER_EXPERIENCE}
						key={contentId}
						attributes={successAttributes}
					/>
				)}
			{/* Note that we need to look at the query data state instead of the "loading" state because
  the "cache-and-network" fetch policy will return the cached data and then go back to the
  "loading" state to pull the current data from the network - but the cached data is still
  returned until the network fetch is completed. But if we have "!noteDataLoading" here
  then the banner would flicker when we return to the "loading" state. */}
			{isStatusDataAvailable && isContentArchived && noteDataContent ? (
				<LazyArchivedPageBannerContent
					contentId={contentId}
					marginStyle={marginStyle}
					userCanRestorePage={userCanRestorePage}
					archiveNote={archiveNote}
					archiverAccountId={archiverAccountId}
					getUnarchiveDialogOpener={getUnarchiveDialogOpener}
					fireLearnMoreEvent={fireLearnMoreEvent}
				/>
			) : null}
		</>
	);
};

type ArchivedPageBannerMetadataFragmentReaderProps = ArchivedPageBannerStatusProps & {
	contentFragmentRef: ArchivedPageBannerV2_content$key;
	noteDataError?: Error;
};

const ArchivedPageBannerRelayMetadataFragmentReader = ({
	contentFragmentRef,
	...bannerProps
}: ArchivedPageBannerMetadataFragmentReaderProps): React.JSX.Element => {
	const archivedContent = useFragment(ArchivedPageBannerV2_content, contentFragmentRef);

	return (
		<ArchivedPageBannerWithMetadata
			{...bannerProps}
			noteDataContent={
				archivedContent ?? (contentFragmentRef as unknown as ArchivedPageBannerV2_content$data)
			}
		/>
	);
};

const ArchivedPageBannerLegacyMetadataReader = ({
	contentFragmentRef,
	...bannerProps
}: ArchivedPageBannerMetadataFragmentReaderProps): React.JSX.Element => (
	<ArchivedPageBannerWithMetadata
		{...bannerProps}
		noteDataContent={contentFragmentRef as unknown as ArchivedPageBannerV2_content$data}
	/>
);

const ArchivedPageBannerMetadataFragmentReader = (
	props: ArchivedPageBannerMetadataFragmentReaderProps,
): React.JSX.Element =>
	hasFragmentSpread(props.contentFragmentRef) ||
	!hasDirectField(props.contentFragmentRef, 'archivedContentMetadata') ? (
		<ArchivedPageBannerRelayMetadataFragmentReader {...props} />
	) : (
		<ArchivedPageBannerLegacyMetadataReader {...props} />
	);

const ArchivedPageBannerWithStatus = ({
	contentId,
	marginStyle = 'bottom',
	contentStatus,
	statusContentId,
	statusError,
	statusRefetch,
	useMetadataFragmentReader,
	onPageRestore,
}: ArchivedPageBannerStatusProps) => {
	const isContentArchived =
		isMatchingContentId(statusContentId, contentId) && contentStatus === CONTENT_STATUS_ARCHIVED;

	// If the content is not archived, then we MUST NOT do the second query for the archive note
	// info because that query will fail!
	const { error: noteDataError, data: noteData } = useQueryInterop<
		ArchivedPageBannerV2ArchivedPageBannerQuery$data,
		ArchivedPageBannerV2ArchivedPageBannerQuery$variables
	>(
		graphql`
			query ArchivedPageBannerV2ArchivedPageBannerQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current"]) {
					nodes {
						id
						type
						operations {
							operation
							targetType
						}
						archivedContentMetadata {
							archiveNote
						}
						history {
							lastUpdated {
								by {
									... on KnownUser {
										accountId
									}
									... on UnknownUser {
										accountId
									}
								}
							}
						}
						...ArchivedPageBannerV2_content
					}
				}
			}
		`,
		{
			variables: { contentId },
			skip: !isContentArchived,
			fetchPolicy: 'cache-and-network',
		},
	);

	const noteDataContent = noteData?.content?.nodes?.[0];

	if (noteDataContent && useMetadataFragmentReader) {
		return (
			<ArchivedPageBannerMetadataFragmentReader
				contentId={contentId}
				marginStyle={marginStyle}
				contentStatus={contentStatus}
				statusContentId={statusContentId}
				statusError={statusError}
				statusRefetch={statusRefetch}
				onPageRestore={onPageRestore}
				noteDataError={noteDataError}
				contentFragmentRef={noteDataContent}
			/>
		);
	}

	return (
		<ArchivedPageBannerWithMetadata
			contentId={contentId}
			marginStyle={marginStyle}
			contentStatus={contentStatus}
			statusContentId={statusContentId}
			statusError={statusError}
			statusRefetch={statusRefetch}
			onPageRestore={onPageRestore}
			noteDataError={noteDataError}
			noteDataContent={noteDataContent}
		/>
	);
};

const ArchivedPageBannerQuery = ({
	contentId,
	marginStyle = 'bottom',
	onPageRestore,
}: ArchivedPageBannerProps) => {
	const {
		error: statusError,
		data: statusData,
		refetch: statusRefetch,
	} = useQueryInterop<
		ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data,
		ArchivedPageBannerV2ArchivedPageBannerContainerQuery$variables
	>(
		graphql`
			query ArchivedPageBannerV2ArchivedPageBannerContainerQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current"]) {
					nodes {
						id
						status
					}
				}
			}
		`,
		{
			variables: { contentId },
			fetchPolicy: 'cache-and-network',
		},
	);

	const contentStatus = statusData?.content?.nodes?.[0]?.status;

	return (
		<ArchivedPageBannerWithStatus
			contentId={contentId}
			marginStyle={marginStyle}
			contentStatus={contentStatus}
			statusContentId={statusData?.content?.nodes?.[0]?.id}
			statusError={statusError}
			statusRefetch={statusRefetch}
			onPageRestore={onPageRestore}
		/>
	);
};

type ArchivedPageBannerStatusFragmentReaderProps = ArchivedPageBannerProps & {
	contentFragmentRef: ArchivedPageBannerV2Status_content$key;
};

const ArchivedPageBannerRelayStatusFragmentReader = ({
	contentId,
	marginStyle = 'bottom',
	contentFragmentRef,
	onPageRestore,
}: ArchivedPageBannerStatusFragmentReaderProps): React.JSX.Element => {
	const content = useFragment(ArchivedPageBannerV2Status_content, contentFragmentRef);
	const directContent = contentFragmentRef as unknown as ArchivedPageBannerV2Status_content$data;

	return (
		<ArchivedPageBannerWithStatus
			contentId={contentId}
			marginStyle={marginStyle}
			contentStatus={content?.status ?? directContent?.status}
			statusContentId={content?.id ?? directContent?.id}
			useMetadataFragmentReader
			onPageRestore={onPageRestore}
		/>
	);
};

const ArchivedPageBannerLegacyStatusReader = ({
	contentId,
	marginStyle = 'bottom',
	contentFragmentRef,
	onPageRestore,
}: ArchivedPageBannerStatusFragmentReaderProps): React.JSX.Element => {
	const content = contentFragmentRef as unknown as ArchivedPageBannerV2Status_content$data;

	return (
		<ArchivedPageBannerWithStatus
			contentId={contentId}
			marginStyle={marginStyle}
			contentStatus={content?.status}
			statusContentId={content?.id}
			useMetadataFragmentReader
			onPageRestore={onPageRestore}
		/>
	);
};

const ArchivedPageBannerStatusFragmentReader = (
	props: ArchivedPageBannerStatusFragmentReaderProps,
): React.JSX.Element =>
	hasFragmentSpread(props.contentFragmentRef) ||
	!hasDirectField(props.contentFragmentRef, 'status') ? (
		<ArchivedPageBannerRelayStatusFragmentReader {...props} />
	) : (
		<ArchivedPageBannerLegacyStatusReader {...props} />
	);

const ArchivedPageBannerComponent = (props: ArchivedPageBannerProps) => {
	const { contentFragmentRef } = props;

	if (
		contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<ArchivedPageBannerStatusFragmentReader {...props} contentFragmentRef={contentFragmentRef} />
		);
	}

	return <ArchivedPageBannerQuery {...props} />;
};

export const ArchivedPageBanner = ({
	contentId,
	marginStyle,
	contentFragmentRef,
	onPageRestore,
}: ArchivedPageBannerProps): React.JSX.Element => (
	<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
		{/* Using a "key" prop with the "contentId" here will make React create a new ExperienceStart
        instance whenever the contentId changes. This is useful because we can remain mounted
        when the user changes to a different content item but we will want to start tracking the
        new page's banner as a new experience. */}
		<ExperienceStart name={ARCHIVED_PAGE_BANNER_EXPERIENCE} key={contentId} />
		<ArchivedPageBannerComponent
			contentId={contentId}
			marginStyle={marginStyle}
			contentFragmentRef={contentFragmentRef}
			onPageRestore={onPageRestore}
		/>
	</ErrorBoundary>
);
